import { valtioDevTools } from '@lib/utils/devTools';
import { proxy } from 'valtio';
import { AudioDevice, DeviceFailureResult, ErrorCategory } from './types';

export type AudioDevicesState = {
  /** List of available audio input (microphone) devices */
  audioInputDevices: AudioDevice[];
  /** List of available audio output (speaker) devices */
  audioOutputDevices: AudioDevice[];
  /** Device ID of the currently active audio input device, undefined when not initialized */
  currentInputDeviceId: string | undefined;
  /** Device ID of the currently active audio output device, undefined when not initialized */
  currentOutputDeviceId: string | undefined;
  /** Device ID of the currently active ring output device, undefined when not initialized */
  currentRingDeviceId: string | undefined;
  /** Web Audio API reference for visualization */
  microphoneAnalyser: AnalyserNode | null;
  /** Whether devices have been enumerated and are ready for use */
  isDevicesReady: boolean;
  /** Error category if a device operation failed, null otherwise */
  errorCategory: ErrorCategory | null;
  /** Whether the microphone test is currently active */
  isTestingMicrophone: boolean;
  /** Whether the speaker test is currently active */
  isTestingSpeaker: boolean;
  /** Error message from the last failed speaker test, null when no error */
  speakerTestError: string | null;
  /** Whether the ring output device intentionally differs from the call output device */
  isRingOutputEnabled: boolean;
  /** Whether the ring speaker test is currently active */
  isTestingRingSpeaker: boolean;
  /** Error message from the last failed ring speaker test, null when no error */
  ringSpeakerTestError: string | null;
  /** Last device switch failure details, null when no error */
  lastSetDevicesError: {
    audioInput: DeviceFailureResult;
    audioOutput: DeviceFailureResult;
    ringOutput: DeviceFailureResult;
  } | null;
  /** Whether the device initialization failure banner has been dismissed by the user */
  dismissedInitFailed: boolean;
  /** Whether the hot-unplug banner has been dismissed by the user */
  dismissedHotUnplug: boolean;
};

export const audioDevicesState = proxy<AudioDevicesState>({
  audioInputDevices: [],
  audioOutputDevices: [],
  currentInputDeviceId: undefined,
  currentOutputDeviceId: undefined,
  currentRingDeviceId: undefined,
  microphoneAnalyser: null as AnalyserNode | null,
  isDevicesReady: false,
  errorCategory: null,
  isTestingMicrophone: false,
  isTestingSpeaker: false,
  speakerTestError: null,
  isRingOutputEnabled: false,
  isTestingRingSpeaker: false,
  ringSpeakerTestError: null,
  lastSetDevicesError: null,
  dismissedInitFailed: false,
  dismissedHotUnplug: false,
});

/** Resets transient operational state while preserving device lists and selected device IDs. */
export const resetAudioDevicesState = (): void => {
  audioDevicesState.microphoneAnalyser = null;
  audioDevicesState.isDevicesReady = false;
  audioDevicesState.errorCategory = null;
  audioDevicesState.isTestingMicrophone = false;
  audioDevicesState.isTestingSpeaker = false;
  audioDevicesState.speakerTestError = null;
  audioDevicesState.isTestingRingSpeaker = false;
  audioDevicesState.ringSpeakerTestError = null;
  audioDevicesState.lastSetDevicesError = null;
  // Reset dismiss flags so banners can reappear if conditions recur after reconnect.
  // Safe to do unconditionally: isDevicesReady = false and errorCategory = null (above)
  // already suppress both banners regardless of these values.
  audioDevicesState.dismissedInitFailed = false;
  audioDevicesState.dismissedHotUnplug = false;
};

valtioDevTools(audioDevicesState, 'audioDevicesState');
